import type { InfiniteData } from '@tanstack/react-query';
import type { Message, TimelineResponse } from '@cyphex/types/chat';
import type { MessageId } from '@cyphex/types/common';

/** Every cache under POST_KEYS.all is either a paged timeline or a flat list. */
export type PostCache = InfiniteData<TimelineResponse> | Message[] | undefined;

/**
 * Patch one message wherever it lives (feed page, thread page, pinned list) so a
 * single `setQueriesData` call keeps every surface consistent without refetching.
 */
export function patchMessage(
  data: PostCache,
  messageId: MessageId,
  patch: (message: Message) => Message,
): PostCache {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map((message) => (message.id === messageId ? patch(message) : message));
  }

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.map((message) =>
        message.id === messageId ? patch(message) : message,
      ),
    })),
  };
}

/** Append a just-sent message to the newest page (page 0 of a keyset feed). */
export function appendMessage(
  data: InfiniteData<TimelineResponse> | undefined,
  message: Message,
): InfiniteData<TimelineResponse> | undefined {
  if (!data || data.pages.length === 0) return data;
  return {
    ...data,
    pages: data.pages.map((page, index) =>
      index === 0 ? { ...page, messages: [...page.messages, message], lastSeq: message.seq } : page,
    ),
  };
}

export function removeMessage(
  data: InfiniteData<TimelineResponse> | undefined,
  messageId: MessageId,
): InfiniteData<TimelineResponse> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      messages: page.messages.filter((message) => message.id !== messageId),
    })),
  };
}
